import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormaNaHora, Supplier } from '@core/types';
import Layout from '../componentes/Layout';
import Aviso from '../componentes/Aviso';
import Carregando from '../componentes/Carregando';
import {
  comprasNaHora, desfazerCompraNaHora, fornecedores, registrarCompraNaHora,
} from '../lib/consultas';
import {
  FORMAS_OUTRAS, FORMAS_PRINCIPAIS, dentroDaJanela, lerEstadoDaEntrada, limparEstadoDaEntrada, nomeDaCompra,
  paraEnvio, precisaConfirmar, resumoDoLancado, resumoDoMes, rotuloDaForma, rotuloDoBotao,
  validarCompraNaHora, valorDaCompra, type CamposDaCompra, type EnvioDaCompra,
} from '../lib/compraNaHora';
import { brl, dataSoDiaBR, hojeISO, mesISO } from '../lib/formato';
import { podeVerFinancas, useSessao } from '../lib/sessao';
import { mensagemDeErro } from '../lib/supabase';

// =============================================================================
// "PAGUEI FORNECEDOR" — a compra paga na hora (migração 095, 08/10/2026)
//
// O dono paga de R$ 15 a 19 mil por mês a fornecedor NA HORA, em dinheiro ou
// pix, e esse dinheiro não ficava em lugar nenhum: o "+ Lançar" do Caixa é um
// formulário de seis campos, a gaveta nunca foi aberta, e a Entrada só lança
// com o custo de cada peça digitado. Esta tela é o caminho de 10 segundos: um
// toque no fornecedor, o valor, um toque na forma, Lançar.
//
// A compra vira uma conta de fornecedor JÁ PAGA (registrar_compra_na_hora):
// aparece em Contas a pagar → Pagas e no Caixa como Compra de mercadoria.
//
// QUEM LANÇA: gerente e dono. QUEM VÊ O MÊS: só o dono (podeVerFinancas) — o
// bloco do mês nem é montado para o gerente, e a consulta dele não dispara.
// A RLS de contas_pagar devolveria vazio de todo jeito; esconder aqui é para
// não oferecer um total que o banco vai negar. Decisão de 28/09/2026:
// faturamento e caixa são do dono.
// =============================================================================

interface Lancado {
  id: string;
  valor: number;
  forma: FormaNaHora;
  nome: string;
  /** Date.now() do lançamento: o "desfazer" do aviso vale 15 minutos. */
  em: number;
}

const CHIP = 'border px-3 py-2 text-sm font-bold';
const CHIP_LIGADO = 'border-mute bg-edge text-paper';
const CHIP_DESLIGADO = 'border-edge text-mute';

export default function CompraNaHora() {
  const { papel } = useSessao();
  const dono = podeVerFinancas(papel);
  const cliente = useQueryClient();
  const hoje = hojeISO();

  // O que a Entrada de mercadoria mandou (fornecedor, total, nota), pelo
  // ESTADO do roteador — nunca pela URL. Lido UMA vez: é o valor inicial do
  // formulário, não uma sincronização (que apagaria o que a pessoa digitou).
  // E, lido, ele sai do histórico (limparEstadoDaEntrada): voltar a esta tela
  // depois de lançar não pode preencher a mesma compra outra vez.
  const local = useLocation();
  const navegar = useNavigate();
  const [daEntrada] = useState(() => lerEstadoDaEntrada(local.state));
  useEffect(() => { limparEstadoDaEntrada(local, navegar); }, [local, navegar]);

  const [fornecedorId, setFornecedorId] = useState<string | null>(daEntrada.fornecedorId);
  const [outro, setOutro] = useState(false);
  const [nome, setNome] = useState('');
  const [valor, setValor] = useState(daEntrada.valor);
  const [forma, setForma] = useState<FormaNaHora | null>(null);
  const [maisFormas, setMaisFormas] = useState(false);
  const [observacao, setObservacao] = useState(daEntrada.observacao);
  const [outroDia, setOutroDia] = useState(false);
  const [data, setData] = useState(hoje);
  // A segunda olhada acima de R$ 5.000: o "35000" no lugar de "350,00".
  const [conferindo, setConferindo] = useState(false);
  const [ultimo, setUltimo] = useState<Lancado | null>(null);
  const [desfazendoUltimo, setDesfazendoUltimo] = useState(false);
  const [agora, setAgora] = useState(() => Date.now());

  const lista = useQuery({ queryKey: ['fornecedores'], queryFn: fornecedores, staleTime: 10 * 60_000 });
  const listaDeFornecedores = lista.data ?? [];

  // O relógio do "desfazer" do aviso: de meio em meio minuto, e só enquanto
  // há um lançamento na tela — o balcão fica com a tela ligada o dia inteiro.
  useEffect(() => {
    if (!ultimo) return;
    const id = window.setInterval(() => setAgora(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [ultimo]);

  const campos: CamposDaCompra = {
    fornecedorId, nome: outro ? nome : '', valor, forma, observacao,
    data: dono && outroDia ? data : hoje,
  };
  const falta = validarCompraNaHora(campos, papel, hoje);
  const v = valorDaCompra(valor);
  const nomeEscolhido = fornecedorId
    ? listaDeFornecedores.find((f) => f.id === fornecedorId)?.nome ?? 'fornecedor'
    : nome.trim();

  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ['compras-na-hora'] });
    // A compra é saída no livro e conta paga: as duas telas mudam junto.
    cliente.invalidateQueries({ queryKey: ['caixa'] });
    cliente.invalidateQueries({ queryKey: ['contas-pagar'] });
  };

  const lancar = useMutation({
    mutationFn: (p: { envio: EnvioDaCompra; nome: string }) => registrarCompraNaHora(p.envio),
    onSuccess: (id, p) => {
      setUltimo({ id, valor: p.envio.valor, forma: p.envio.forma, nome: p.nome, em: Date.now() });
      setAgora(Date.now());
      setDesfazendoUltimo(false);
      // Limpa para a próxima — e MANTÉM a forma: quem pagou um no pix
      // costuma pagar o seguinte no pix.
      setFornecedorId(null);
      setOutro(false);
      setNome('');
      setValor('');
      setObservacao('');
      setOutroDia(false);
      setData(hoje);
      setConferindo(false);
      recarregar();
    },
  });

  const desfazerUltimo = useMutation({
    mutationFn: desfazerCompraNaHora,
    onSuccess: () => { setUltimo(null); setDesfazendoUltimo(false); recarregar(); },
  });

  const enviar = () => {
    // O botão já está desabilitado nos dois casos; isto segura o Enter do
    // teclado e o segundo toque que chega antes de a tela redesenhar.
    if (falta || lancar.isPending) return;
    if (precisaConfirmar(v) && !conferindo) { setConferindo(true); return; }
    lancar.mutate({ envio: paraEnvio(campos, hoje), nome: nomeEscolhido });
  };

  const escolherFornecedor = (f: Supplier) => {
    setOutro(false);
    setFornecedorId(fornecedorId === f.id ? null : f.id);
  };

  return (
    <Layout titulo="Paguei fornecedor">
      <div className="mx-auto max-w-xl space-y-4">
        <p className="text-sm text-mute">
          Compra paga na hora. Boleto continua em Contas a pagar.
        </p>

        {ultimo && (
          <Aviso tipo="ok">
            <span className="font-bold">{resumoDoLancado(ultimo.valor, ultimo.forma, ultimo.nome)}</span>
            {dentroDaJanela(ultimo.em, agora) && !desfazendoUltimo && (
              <button type="button" className="ml-2 text-xs underline"
                      onClick={() => setDesfazendoUltimo(true)}>
                desfazer
              </button>
            )}
            {/* Desfazer APAGA o lançamento do caixa: dois passos, como em
                Contas a pagar. */}
            {desfazendoUltimo && (
              <span className="mt-2 block space-y-2">
                <span className="block text-xs text-mute">
                  Apaga esta compra: sai do caixa e da lista.
                </span>
                <span className="flex gap-2">
                  <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                          onClick={() => setDesfazendoUltimo(false)}>Voltar</button>
                  <button type="button" className="botao-perigo flex-1 py-1.5 text-xs"
                          disabled={desfazerUltimo.isPending}
                          onClick={() => desfazerUltimo.mutate(ultimo.id)}>
                    {desfazerUltimo.isPending ? 'Apagando…' : 'Apagar a compra'}
                  </button>
                </span>
              </span>
            )}
          </Aviso>
        )}
        {desfazerUltimo.error ? <Aviso tipo="erro">{mensagemDeErro(desfazerUltimo.error)}</Aviso> : null}

        <form className="cartao space-y-5 p-4"
              onSubmit={(e) => { e.preventDefault(); enviar(); }}>
          <div>
            <p className="rotulo">De quem?</p>
            {lista.isPending ? <p className="text-xs text-dim">Carregando os fornecedores…</p> : null}
            {lista.error ? <Aviso tipo="erro">{mensagemDeErro(lista.error)}</Aviso> : null}
            <div className="flex flex-wrap gap-2">
              {listaDeFornecedores.map((f) => (
                <button key={f.id} type="button" aria-pressed={fornecedorId === f.id}
                        onClick={() => escolherFornecedor(f)}
                        className={`${CHIP} ${fornecedorId === f.id ? CHIP_LIGADO : CHIP_DESLIGADO}`}>
                  {f.nome}
                </button>
              ))}
              <button type="button" aria-pressed={outro}
                      onClick={() => { setFornecedorId(null); setOutro(!outro); }}
                      className={`${CHIP} ${outro ? CHIP_LIGADO : CHIP_DESLIGADO}`}>
                Outro
              </button>
            </div>
            {outro && (
              <input className="campo mt-2" aria-label="Nome de quem vendeu"
                     placeholder="Nome de quem vendeu" autoComplete="off"
                     value={nome} onChange={(e) => setNome(e.target.value)} />
            )}
          </div>

          <div>
            <label className="rotulo" htmlFor="cnh-valor">Quanto? (R$)</label>
            {/* paraNumero na conversão, sempre (regra 9): "1.500" é mil e
                quinhentos, não um e meio. */}
            <input id="cnh-valor" className="campo text-2xl font-black tabular-nums"
                   inputMode="decimal" placeholder="0,00" autoComplete="off" autoFocus
                   value={valor}
                   onChange={(e) => { setValor(e.target.value); setConferindo(false); }} />
          </div>

          <div>
            <p className="rotulo">Como pagou?</p>
            <div className="grid grid-cols-2 gap-2">
              {FORMAS_PRINCIPAIS.map((f) => (
                <button key={f.id} type="button" aria-pressed={forma === f.id}
                        onClick={() => setForma(f.id)}
                        className={`border px-4 py-4 text-lg font-bold ${forma === f.id ? CHIP_LIGADO : CHIP_DESLIGADO}`}>
                  {f.rotulo}
                </button>
              ))}
            </div>
            {maisFormas || FORMAS_OUTRAS.some((f) => f.id === forma) ? (
              <div className="mt-2 grid grid-cols-3 gap-2">
                {FORMAS_OUTRAS.map((f) => (
                  <button key={f.id} type="button" aria-pressed={forma === f.id}
                          onClick={() => setForma(f.id)}
                          className={`${CHIP} px-2 text-xs ${forma === f.id ? CHIP_LIGADO : CHIP_DESLIGADO}`}>
                    {f.rotulo}
                  </button>
                ))}
              </div>
            ) : (
              <button type="button" className="mt-2 text-xs text-dim underline"
                      onClick={() => setMaisFormas(true)}>
                outra forma
              </button>
            )}
          </div>

          <div>
            <label className="rotulo" htmlFor="cnh-obs">O que veio (opcional)</label>
            <input id="cnh-obs" className="campo" autoComplete="off"
                   placeholder="NF 1234, 2 pastilhas, óleo…"
                   value={observacao} onChange={(e) => setObservacao(e.target.value)} />
          </div>

          {/* Dia passado é só do dono — a mesma régua do banco. O gerente
              lança o de hoje, sem ver este campo. */}
          {dono && (outroDia ? (
            <div>
              <label className="rotulo" htmlFor="cnh-data">Dia da compra</label>
              <input id="cnh-data" type="date" className="campo" max={hoje}
                     value={data} onChange={(e) => setData(e.target.value)} />
            </div>
          ) : (
            <button type="button" className="text-xs text-dim underline"
                    onClick={() => setOutroDia(true)}>
              foi outro dia?
            </button>
          ))}

          {conferindo && v !== null ? (
            <div className="space-y-2 border border-alerta/50 bg-alerta/10 p-3">
              <p className="text-base font-bold text-alerta">Confere: {brl(v)}?</p>
              <p className="text-xs text-mute">É um valor alto — veja se não sobrou um zero.</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" className="botao-secundario py-3"
                        onClick={() => setConferindo(false)}>Corrigir</button>
                <button type="button" className="botao-primario py-3"
                        disabled={lancar.isPending} onClick={enviar}>
                  {lancar.isPending ? 'Lançando…' : 'Sim, lançar'}
                </button>
              </div>
            </div>
          ) : (
            <button type="submit" className="botao-primario w-full py-4 text-base"
                    disabled={Boolean(falta) || lancar.isPending}>
              {rotuloDoBotao({ valor, forma }, lancar.isPending)}
            </button>
          )}
          {falta && !lancar.isPending ? <p className="text-xs text-dim">{falta}</p> : null}
          {lancar.error ? <Aviso tipo="erro">{mensagemDeErro(lancar.error)}</Aviso> : null}
        </form>

        {dono ? (
          <MesDasCompras listaDeFornecedores={listaDeFornecedores} />
        ) : (
          <p className="text-xs text-dim">
            O dono vê o que foi lançado em Contas a pagar. Errou? Use o
            “desfazer” do aviso, nos primeiros 15 minutos.
          </p>
        )}
      </div>
    </Layout>
  );
}

/**
 * O MÊS — só do dono. Montado só quando `podeVerFinancas`: para o gerente o
 * componente não existe, e a consulta não chega a ser criada.
 */
function MesDasCompras({ listaDeFornecedores }: { listaDeFornecedores: Supplier[] }) {
  const cliente = useQueryClient();
  const [mes, setMes] = useState(() => mesISO());
  const [desfazendo, setDesfazendo] = useState<string | null>(null);

  const consulta = useQuery({ queryKey: ['compras-na-hora', mes], queryFn: () => comprasNaHora(mes) });
  const nomes = useMemo(
    () => new Map(listaDeFornecedores.map((f) => [f.id, f.nome] as const)),
    [listaDeFornecedores],
  );
  const linhas = consulta.data ?? [];
  const resumo = useMemo(() => resumoDoMes(consulta.data ?? [], nomes), [consulta.data, nomes]);

  const desfazer = useMutation({
    mutationFn: desfazerCompraNaHora,
    onSuccess: () => {
      setDesfazendo(null);
      cliente.invalidateQueries({ queryKey: ['compras-na-hora'] });
      cliente.invalidateQueries({ queryKey: ['caixa'] });
      cliente.invalidateQueries({ queryKey: ['contas-pagar'] });
    },
  });

  const mesAtual = mesISO();
  const mesBR = `${mes.slice(5)}/${mes.slice(0, 4)}`;
  const passoMes = (n: number) => {
    const d = new Date(`${mes}-15T12:00:00`);
    d.setMonth(d.getMonth() + n);
    setMes(mesISO(d));
    setDesfazendo(null);
  };

  return (
    <section className="cartao space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-bold">Compras pagas na hora</h2>
        <div className="flex items-center gap-2">
          <button type="button" className="botao-secundario px-2 py-1"
                  aria-label="Mês anterior" onClick={() => passoMes(-1)}>←</button>
          <span className="text-sm tabular-nums text-mute">{mesBR}</span>
          <button type="button" className="botao-secundario px-2 py-1"
                  aria-label="Próximo mês" disabled={mes >= mesAtual}
                  onClick={() => passoMes(1)}>→</button>
        </div>
      </div>

      {consulta.isPending ? <Carregando texto="Somando o mês" /> : null}
      {consulta.error ? <Aviso tipo="erro">{mensagemDeErro(consulta.error)}</Aviso> : null}

      {!consulta.isPending && (
        <>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-3xl font-black tabular-nums">{brl(resumo.total)}</p>
            <p className="text-xs text-dim">
              {resumo.quantas} compra{resumo.quantas === 1 ? '' : 's'} em {mesBR}
            </p>
          </div>

          {resumo.porForma.length > 0 && (
            <ul className="flex flex-wrap gap-2 text-sm">
              {resumo.porForma.map((f) => (
                <li key={f.forma ?? '-'} className="border border-edge px-2.5 py-1">
                  <span className="text-dim">{rotuloDaForma(f.forma)}</span>{' '}
                  <strong className="tabular-nums">{brl(f.valor)}</strong>
                </li>
              ))}
            </ul>
          )}

          {resumo.porFornecedor.length > 0 && (
            <div>
              <h3 className="rotulo">Por fornecedor</h3>
              <ul className="divide-y divide-edge">
                {resumo.porFornecedor.map((g) => (
                  <li key={g.chave} className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
                    <span className="min-w-0 truncate">{g.nome}</span>
                    <span className="shrink-0 tabular-nums">
                      <strong>{brl(g.valor)}</strong>
                      <span className="text-xs text-dim"> · {g.quantas}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {linhas.length > 0 ? (
            <div>
              <h3 className="rotulo">Uma a uma</h3>
              <ul className="space-y-2">
                {linhas.map((c) => (
                  <li key={c.id} className="border border-edge p-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold">
                          {(c.fornecedor_id && nomes.get(c.fornecedor_id)) || nomeDaCompra(c)}
                        </p>
                        <p className="text-xs text-mute">
                          {c.pago_em ? dataSoDiaBR(c.pago_em).slice(0, 5) : '—'}
                          {' · '}{rotuloDaForma(c.forma_pagamento)}
                          {c.pago_por_nome ? ` · por ${c.pago_por_nome}` : ''}
                        </p>
                        {c.observacao && <p className="text-xs text-dim">{c.observacao}</p>}
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="font-black tabular-nums">{brl(Number(c.valor_real ?? 0))}</p>
                        <button type="button" className="text-xs text-dim underline"
                                onClick={() => setDesfazendo(desfazendo === c.id ? null : c.id)}>
                          desfazer
                        </button>
                      </div>
                    </div>
                    {desfazendo === c.id && (
                      <div className="mt-2 space-y-2 border border-erro/40 bg-erro/5 p-3">
                        <p className="text-xs text-mute">
                          Apaga esta compra: sai do caixa e desta lista.
                        </p>
                        <div className="flex gap-2">
                          <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                                  onClick={() => setDesfazendo(null)}>Voltar</button>
                          <button type="button" className="botao-perigo flex-1 py-1.5 text-xs"
                                  disabled={desfazer.isPending}
                                  onClick={() => desfazer.mutate(c.id)}>
                            Apagar a compra
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-dim">Nenhuma compra paga na hora em {mesBR}.</p>
          )}
          {desfazer.error ? <Aviso tipo="erro">{mensagemDeErro(desfazer.error)}</Aviso> : null}
        </>
      )}

      <p className="text-xs text-dim">
        Também aparecem em <Link to="/contas" className="underline">Contas a pagar → Pagas</Link>
        {' '}e no <Link to="/caixa" className="underline">Caixa</Link> como Compra de mercadoria.
      </p>
    </section>
  );
}
